import React, { useState } from "react";
import {
  Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from "recharts";

const SERIES = [
  { key: "revenue_net", label: "Revenue (net)", colour: "#22c55e", type: "bar" },
  { key: "cost_of_sales", label: "Cost of sales", colour: "#f97316", type: "bar" },
  { key: "operating_expenses", label: "Overheads", colour: "#64748b", type: "bar" },
  { key: "net_profit", label: "Net profit", colour: "#38bdf8", type: "line" },
];
const money = (v) => `€${Number(v || 0).toLocaleString("en-IE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Y-axis ticks. The old formatter was `€${Math.round(v / 1000)}k`, which
// collapsed every value under €1,500 onto "€0k" / "€1k" — so a chart
// scaled to €2,000 rendered "€1k, €1k, €0k, €0k". Ticks are now exact
// and grouped (€-1,000 · €-500 · €0 · €500 · €1,000 · €1,500 · €2,000),
// so two different ticks can never carry the same label.
const axisTick = (v) => {
  const n = Number(v) || 0;
  return `€${n.toLocaleString("en-IE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: Number.isInteger(n) ? 0 : 2,
  })}`;
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded border border-slate-700 bg-slate-900/95 px-3 py-2 text-xs"
         data-testid="finance-chart-tooltip">
      <p className="text-white font-semibold mb-1">{label}</p>
      {SERIES.map((s) => (
        <p key={s.key} className="flex justify-between gap-4">
          <span style={{ color: s.colour }}>{s.label}</span>
          <span className="font-mono text-slate-200">{money(row[s.key])}</span>
        </p>
      ))}
    </div>
  );
};

export const PnlChart = ({ buckets = [], granularity, onMonthClick }) => {
  const [hidden, setHidden] = useState({});
  const toggle = (key) => setHidden((h) => ({ ...h, [key]: !h[key] }));

  if (!buckets.length) {
    return <p className="text-xs text-slate-500" data-testid="finance-chart-empty">
      Nothing to chart for this period.
    </p>;
  }

  return (
    <div data-testid="finance-chart">
      <p className="text-xs text-slate-500 mb-2">
        {granularity === "week" ? "Weekly" : "Monthly"} revenue vs costs vs profit
        {granularity !== "week" && " · click a month to filter by it"}
      </p>
      <div className="h-[260px] sm:h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={buckets} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
            <XAxis dataKey="label" stroke="#64748b" fontSize={11} tickLine={false} />
            <YAxis stroke="#64748b" fontSize={11} tickLine={false} width={72}
                   tickFormatter={axisTick} data-testid="finance-chart-yaxis" />
            <Tooltip content={<ChartTooltip />} cursor={{ fill: "#1e293b55" }} />
            <Legend
              onClick={(e) => toggle(e.dataKey)}
              wrapperStyle={{ fontSize: 11, cursor: "pointer" }}
              formatter={(v, entry) => (
                <span style={{ color: hidden[entry.dataKey] ? "#475569" : "#cbd5e1" }}>{v}</span>
              )}
            />
            {SERIES.filter((s) => s.type === "bar").map((s) => (
              <Bar key={s.key} dataKey={s.key} name={s.label} fill={s.colour}
                   hide={!!hidden[s.key]} radius={[2, 2, 0, 0]}
                   onClick={(d) => d?.payload?.month && onMonthClick?.(d.payload.month)}
                   cursor={granularity === "week" ? "default" : "pointer"} />
            ))}
            {SERIES.filter((s) => s.type === "line").map((s) => (
              <Line key={s.key} dataKey={s.key} name={s.label} stroke={s.colour}
                    strokeWidth={2} dot={{ r: 2 }} hide={!!hidden[s.key]} />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <table className="sr-only">
        <caption>Revenue, cost of sales, overheads and net profit per period</caption>
        <tbody>
          {buckets.map((b) => (
            <tr key={b.key}>
              <th scope="row">{b.label}</th>
              {SERIES.map((s) => <td key={s.key}>{s.label}: {money(b[s.key])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PnlChart;
